/** Which shorts engine a job runs. Mirrors PipelineSettings.mode on the backend. */
export type ShortsMode = 'classic' | 'studio';

export const SHORTS_MODE_COPY: Record<ShortsMode, { label: string; help: string }> = {
  classic: {
    label: 'Classic',
    help: 'Quick. Cuts the video into a full set of evenly-spaced clips — you always get options to pick from.',
  },
  studio: {
    label: 'Studio',
    help: 'Finds complete moments, scores them, and may return fewer — or none. Rewrites titles and captions from the final cut. Adds ~1–2 min.',
  },
};

export const SHORTS_MODES: ShortsMode[] = ['classic', 'studio'];

/** Pre-selected in every shorts entry point; the author can still pick Classic. */
export const DEFAULT_SHORTS_MODE: ShortsMode = 'studio';

/** Which reframe strategy crops shorts to vertical. Mirrors PipelineSettings.reframeMode
 *  on the backend. Applies to both Classic and Studio (not the separate manual-clip mode). */
export type ReframeMode = 'active-speaker' | 'fit-all-faces';

export const REFRAME_MODE_COPY: Record<ReframeMode, { label: string; help: string }> = {
  'active-speaker': {
    label: 'Active speaker',
    help: 'Tracks who is talking and crops to follow them, even when multiple people are on screen.',
  },
  'fit-all-faces': {
    label: 'Fit all faces',
    help: 'Widens the crop to keep everyone in frame at once — falls back to letterboxing if they don’t fit.',
  },
};

export const REFRAME_MODES: ReframeMode[] = ['active-speaker', 'fit-all-faces'];

/** Spoken-language choices for transcription. Shared by every shorts / caption entry point. */
export const LANGUAGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'auto', label: 'Auto Language Detect' },
  { value: 'hi', label: 'Hindi (हिन्दी)' },
  { value: 'en', label: 'English' },
  { value: 'hinglish', label: 'Hinglish (Hindi + English)' },
  // The remaining Indian languages the transcribe stage recognises by name, A-Z.
  { value: 'as', label: 'Assamese (অসমীয়া)' },
  { value: 'bn', label: 'Bengali (বাংলা)' },
  { value: 'gu', label: 'Gujarati (ગુજરાતી)' },
  { value: 'kn', label: 'Kannada (ಕನ್ನಡ)' },
  { value: 'ml', label: 'Malayalam (മലയാളം)' },
  { value: 'mr', label: 'Marathi (मराठी)' },
  { value: 'ne', label: 'Nepali (नेपाली)' },
  { value: 'or', label: 'Odia (ଓଡ଼ିଆ)' },
  { value: 'pa', label: 'Punjabi (ਪੰਜਾਬੀ)' },
  { value: 'sa', label: 'Sanskrit (संस्कृतम्)' },
  { value: 'sd', label: 'Sindhi (سنڌي)' },
  { value: 'si', label: 'Sinhala (සිංහල)' },
  { value: 'ta', label: 'Tamil (தமிழ்)' },
  { value: 'te', label: 'Telugu (తెలుగు)' },
  { value: 'ur', label: 'Urdu (اردو)' },
];

/** A Whisper model as returned by GET /api/whisper-models — a name plus an optional size hint. */
export interface WhisperModelOption {
  name: string;
  sizeFormatted?: string;
}

/** What the transcript dropdown shows instead of the Whisper model name. Unknown models fall
 *  back to their raw name, so a newly installed model still appears. */
const WHISPER_MODEL_LABELS: Record<string, string> = {
  'base.en': 'Fast · English only',
  base: 'Fast',
  small: 'Balanced',
  medium: 'Accurate',
  large: 'Best quality (slowest)',
  'large-v3': 'Best quality (slowest)',
};

export const whisperModelLabel = (name: string): string => WHISPER_MODEL_LABELS[name] || name;

export const FALLBACK_WHISPER_MODELS: WhisperModelOption[] = [
  { name: 'base' },
  { name: 'base.en', sizeFormatted: 'fast' },
  { name: 'small' },
  { name: 'medium', sizeFormatted: 'balanced' },
  { name: 'large-v3', sizeFormatted: 'slowest' },
];

/** One-line field explanations shown in the ⓘ tips across every shorts / caption entry point. */
export const SHORTS_TIP = {
  language:
    'The language spoken in the video. Auto-detect suits most clips — set it manually if detection has been wrong, or the audio is short or noisy. Hinglish handles mixed Hindi–English speech.',
  model:
    'Speech-to-text model, used for this job only. base.en is fastest; medium and large-v3 are slower but sharper on accents, names, and background noise.',
  shortsMode:
    'How the video is cut into shorts. Classic is fast and always returns a full set; Studio is selective and slower.',
  reframeMode:
    'How each short is cropped to vertical. Active speaker follows whoever is talking; Fit all faces keeps everyone in frame at once.',
  summary:
    'Adds one extra short: the highest-scoring moments stitched together in the order they happened.',
  videoFile:
    'MP4, MOV, or WebM. This is the source we transcribe and cut — nothing is published until you choose to.',
} as const;
